export async function searchDocuments({ query, language, documentType, year }) {
  const params = new URLSearchParams({ q: query });
  if (language) params.set("language", language);
  if (documentType) params.set("document_type", documentType);
  if (year) params.set("year_be", String(year));
  const response = await fetch(`/api/search?${params}`);
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export async function loadCatalog({ language = "th" } = {}) {
  const params = new URLSearchParams({ language });
  const response = await fetch(`/api/catalog?${params}`);
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export async function loadLibraryCatalog({ language = "th" } = {}) {
  if (language !== "th" && language !== "en") throw new Error("language must be th or en");
  const languages = language === "en" ? ["en", "th"] : ["th", "en"];
  const catalogs = await Promise.all(languages.map((catalogLanguage) => loadCatalog({ language: catalogLanguage })));
  const types = new Map();

  for (const catalog of catalogs) {
    for (const type of catalog.document_types || []) {
      const current = types.get(type.document_type) || { ...type, document_count: 0, years: [] };
      current.document_count += Number(type.document_count) || 0;
      const years = new Map(current.years.map((year) => [year.year_be == null ? "undated" : String(year.year_be), year]));
      for (const year of type.years || []) {
        const key = year.year_be == null ? "undated" : String(year.year_be);
        const existing = years.get(key);
        if (existing) existing.document_count += Number(year.document_count) || 0;
        else years.set(key, { ...year, document_count: Number(year.document_count) || 0 });
      }
      current.years = [...years.values()].sort((left, right) => {
        if (left.year_be == null) return right.year_be == null ? 0 : 1;
        if (right.year_be == null) return -1;
        return Number(right.year_be) - Number(left.year_be);
      });
      types.set(type.document_type, current);
    }
  }

  const document_types = [...types.values()].sort((left, right) => left.document_type.localeCompare(right.document_type, "en"));
  return {
    ...catalogs[0],
    language,
    document_count: document_types.reduce((total, type) => total + type.document_count, 0),
    document_types,
  };
}

export async function loadUpdates() {
  const response = await fetch("/api/updates");
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export async function browseDocuments({
  language = "th",
  documentType,
  yearBe,
  limit = 200,
  offset = 0,
  asOfDate,
}) {
  const params = new URLSearchParams({ language, limit: String(limit), offset: String(offset) });
  if (documentType) params.set("document_type", documentType);
  if (yearBe) params.set("year_be", String(yearBe));
  if (asOfDate) params.set("as_of_date", asOfDate);
  const response = await fetch(`/api/browse?${params}`);
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export async function browseLibraryDocuments({ language = "th", documentType, yearBe, asOfDate } = {}) {
  if (language !== "th" && language !== "en") throw new Error("language must be th or en");
  const languages = language === "en" ? ["en", "th"] : ["th", "en"];
  const pagesPerLanguage = await Promise.all(languages.map(async (browseLanguage) => {
    const results = [];
    for (let page = 0; page < 50; page += 1) {
      const response = await browseDocuments({
        language: browseLanguage,
        documentType,
        yearBe,
        asOfDate,
        limit: 200,
        offset: page * 200,
      });
      if (!Array.isArray(response.results)) throw new Error("Invalid browse response");
      results.push(...response.results);
      if (response.results.length < 200) return results;
    }
    throw new Error(`Browse results exceed 50 pages for ${browseLanguage}`);
  }));

  const byWork = new Map();
  const withoutWorkId = [];
  for (const result of pagesPerLanguage.flat()) {
    if (result.work_id == null || result.work_id === "") {
      withoutWorkId.push(result);
      continue;
    }
    if (!byWork.has(result.work_id)) byWork.set(result.work_id, {
      ...result,
      title: language === "en" ? result.title_en || result.title : result.title,
    });
  }
  return [...byWork.values(), ...withoutWorkId].sort((left, right) => {
    const leftYear = left.year_be == null || left.year_be === "" ? null : Number(left.year_be);
    const rightYear = right.year_be == null || right.year_be === "" ? null : Number(right.year_be);
    const validLeftYear = Number.isFinite(leftYear) ? leftYear : null;
    const validRightYear = Number.isFinite(rightYear) ? rightYear : null;
    if (validLeftYear == null) return validRightYear == null ? 0 : 1;
    if (validRightYear == null) return -1;
    if (validLeftYear !== validRightYear) return validRightYear - validLeftYear;

    const leftDate = typeof left.issued_date === "string" ? left.issued_date : "";
    const rightDate = typeof right.issued_date === "string" ? right.issued_date : "";
    if (!leftDate) return rightDate ? 1 : 0;
    if (!rightDate) return -1;
    return rightDate.localeCompare(leftDate);
  });
}

const sourcePreviewUrls = new Map();

function bindOpaqueSourceRefs(payload) {
  const sources = [payload?.source_item, ...(payload?.source_items || [])].filter(Boolean);
  sources.forEach((source, index) => {
    if (!source.archive_preview_url) return;
    const ref = `${payload.expression?.expression_id || "source"}:${index}`;
    sourcePreviewUrls.set(ref, source.archive_preview_url);
    // Compatibility fields are local opaque handles, never archive hashes.
    source.sha256 = ref;
    source.archive_path = source.archive_url || source.archive_preview_url;
  });
  return payload;
}

export async function loadDocument(expressionId) {
  const params = new URLSearchParams({ expression_id: expressionId });
  const response = await fetch(`/api/document?${params}`);
  if (!response.ok) throw new Error(await response.text());
  return bindOpaqueSourceRefs(await response.json());
}

export async function loadExpressions(workId) {
  const params = new URLSearchParams({ work_id: workId });
  const response = await fetch(`/api/expressions?${params}`);
  if (!response.ok) throw new Error(await response.text());
  return response.json();
}

export async function loadSourcePreview(sha256) {
  const response = await fetch(
    sourcePreviewUrls.get(sha256) || `/api/source/${sha256}/preview`,
  );
  if (!response.ok) throw new Error(await response.text());
  const preview = await response.json();
  // Reader compatibility stays in memory; the public response never exposes
  // a raw checksum field or asks the user to understand archive internals.
  if (preview.archive_preview_url) {
    preview.preview_url = preview.archive_preview_url;
  }
  return preview;
}
